import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Check, ChevronDown } from "lucide-react";
import { usePhaseCollapse } from "@/hooks/usePhaseCollapse";
import PhasesHeader from "@/components/PhasesHeader";
import { PageContainer, PageHeader } from "@/components/layout/PageLayout";
import { GitBranch } from "lucide-react";

const STATUS_META = {
    not_started: { badge: "status-not-started", label: "Not Started" },
    in_progress: { badge: "status-in-progress", label: "In Progress" },
    complete:    { badge: "status-complete",    label: "Complete" },
};

/**
 * Customer Journey — 10-phase vertical timeline matching app/(customer)/journey.tsx.
 * Active phase auto-expands; tap any phase to expand/collapse. Task dots:
 *   • green = complete   • orange = in-progress   • gray = not-started
 */
export default function Journey() {
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const {
        isOpen, toggle, expandAll, collapseAll, allExpanded,
        sectionOpen, toggleSection,
    } = usePhaseCollapse(project?.phases);

    useEffect(() => {
        (async () => {
            try {
                const p = await api.myProject();
                setProject(p);
            } catch {
                setProject(null);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) {
        return <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center text-sm text-[#4B5565]">Loading…</div>;
    }
    if (!project) {
        return <div className="min-h-screen bg-[#F5F5F7] flex items-center justify-center text-sm text-[#4B5565]">No project yet.</div>;
    }

    return (
        <div className="min-h-full bg-[#F5F5F7] pb-16" data-testid="journey-page">
            <PageContainer width="medium">
                <PageHeader
                    eyebrow="CUSTOMER PORTAL · YOUR JOURNEY"
                    title="Your Journey"
                    subtitle={`${project.progress}% complete · ${project.phases.length} phases`}
                    icon={GitBranch}
                    backFallback="/dashboard"
                    testid="journey"
                />

                <PhasesHeader
                    title="Your journey"
                    open={sectionOpen}
                    onToggleSection={toggleSection}
                    allExpanded={allExpanded}
                    onExpandAll={expandAll}
                    onCollapseAll={collapseAll}
                    testidPrefix="journey"
                />

                {sectionOpen && (
                <ol className="space-y-2" data-testid="journey-list">
                    {project.phases.map((phase, idx) => {
                        const meta = STATUS_META[phase.status] || STATUS_META.not_started;
                        const isExpanded = isOpen(phase.id);
                        const doneCount = phase.tasks.filter(t => t.status === "complete").length;
                        const isLast = idx === project.phases.length - 1;
                        const nodeCls = phase.status === "complete"
                            ? "bg-[#3E9E2E] text-white border-white"
                            : phase.status === "in_progress"
                                ? "bg-[#F26522] text-white border-white"
                                : "bg-[#E8E8ED] text-[#4B5565] border-white";
                        const lineCls = phase.status === "complete" ? "bg-[#3E9E2E]" : "bg-[#E5E7EB]";
                        return (
                            <li key={phase.id} className="flex" data-testid={`phase-${idx}`}>
                                {/* Timeline column */}
                                <div className="w-9 flex flex-col items-center">
                                    <div className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-medium ${nodeCls}`}>
                                        {phase.status === "complete" ? <Check size={13} strokeWidth={2.5} /> : idx + 1}
                                    </div>
                                    {!isLast && <div className={`flex-1 w-0.5 ${lineCls} min-h-[16px]`} />}
                                </div>

                                {/* Phase card */}
                                <button
                                    onClick={() => toggle(phase.id)}
                                    className="flex-1 ml-3 mb-1 pe-card pe-card-hover text-left p-4 md:p-5"
                                    data-testid={`phase-toggle-${idx}`}
                                    aria-expanded={isExpanded}
                                    aria-controls={`phase-tasks-${idx}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="flex-1 min-w-0">
                                            <div className="text-base md:text-lg text-[#131B2F] font-medium">{phase.name}</div>
                                            <div className="text-sm text-[#4B5565] mt-0.5">{doneCount}/{phase.tasks.length} tasks</div>
                                        </div>
                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-medium ${meta.badge}`}>
                                            {meta.label}
                                        </span>
                                        <ChevronDown
                                            size={16}
                                            strokeWidth={1.75}
                                            className={`text-[#4B5565] transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                                            aria-hidden="true"
                                        />
                                    </div>

                                    {isExpanded && (
                                        <div id={`phase-tasks-${idx}`} className="mt-4 pt-4 border-t border-[#E5E7EB] space-y-2.5" data-testid={`phase-tasks-${idx}`}>
                                            {phase.tasks.map((t) => {
                                                const tm = STATUS_META[t.status];
                                                const dotCls = t.status === "complete"
                                                    ? "bg-[#3E9E2E] text-white"
                                                    : t.status === "in_progress"
                                                        ? "bg-[#F26522] text-white"
                                                        : "bg-[#D1D5DB]";
                                                return (
                                                    <div key={t.id} className="flex items-center gap-3">
                                                        <span className={`w-[18px] h-[18px] rounded-full flex items-center justify-center shrink-0 ${dotCls}`}>
                                                            {t.status === "complete" && <Check size={11} strokeWidth={3} />}
                                                        </span>
                                                        <span className={`flex-1 text-sm ${t.status === "complete" ? "text-[#4B5565]" : "text-[#131B2F]"}`}>
                                                            {t.name}
                                                        </span>
                                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${tm.badge}`}>
                                                            {tm.label}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </button>
                            </li>
                        );
                    })}
                </ol>
                )}
            </PageContainer>
        </div>
    );
}
